import React, { useEffect, useState } from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Notiflix from "notiflix";

const API_BASE = process.env.REACT_APP_MEMBERSHIP_API || "http://localhost:5001";

const typeIcon = { computer: "🖥️", laptop: "💻", phone: "📱", tablet: "📲", other: "🔌" };
const typeLabel = { computer: "Desktop", laptop: "Laptop", phone: "Phone", tablet: "Tablet", other: "Other" };

const DeviceLocker = () => {
  useDocTitle("ZelT | Device Locker");
  const navigate = useNavigate();
  const [devices, setDevices] = useState(null);
  const [form, setForm] = useState({ nickname: "", deviceType: "laptop", model: "", os: "", notes: "" });
  const [showForm, setShowForm] = useState(false);

  const loadDevices = () => {
    axios
      .get(`${API_BASE}/my-devices`, { headers: { Authorization: `Bearer ${localStorage.getItem("zelt_token")}` } })
      .then((r) => setDevices(r.data.devices))
      .catch(() => setDevices([]));
  };

  useEffect(() => {
    if (!localStorage.getItem("zelt_token")) {
      navigate("/membership");
      return;
    }
    loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addDevice = (e) => {
    e.preventDefault();
    axios
      .post(`${API_BASE}/my-devices`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem("zelt_token")}` },
      })
      .then(() => {
        Notiflix.Notify.success("Device registered 🔐");
        setForm({ nickname: "", deviceType: "laptop", model: "", os: "", notes: "" });
        setShowForm(false);
        loadDevices();
      })
      .catch((err) =>
        Notiflix.Notify.failure(err.response?.data?.message || "Registration failed")
      );
  };

  const removeDevice = (id) => {
    axios
      .post(`${API_BASE}/my-devices/${id}/delete`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("zelt_token")}` },
      })
      .then(() => loadDevices())
      .catch(() => Notiflix.Notify.failure("Delete failed"));
  };

  return (
    <div>
      <NavBar />
      <div className="mt-8 w-full bg-white py-12 lg:py-20">
        <div className="mx-4 max-w-4xl mx-auto">
          <div className="text-center mb-10" data-aos="zoom-in">
            <span className="text-5xl">🔐</span>
            <h1 className="text-4xl font-extrabold text-blue-900 mt-3 mb-3">
              Device Locker
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Register your devices — "John's MacBook Air", "Mom's Windows PC"
              — so your technician knows exactly what they're helping with.
              <b> We never ask for or store passwords.</b>
            </p>
          </div>

          {/* device list */}
          <div className="space-y-4 mb-8" data-aos="fade-up">
            {devices === null && (
              <p className="text-center text-gray-400">Loading your devices…</p>
            )}
            {devices && devices.length === 0 && (
              <div className="bg-gray-50 rounded-2xl p-8 text-center border-2 border-dashed border-gray-200">
                <p className="text-gray-500">
                  🔐 The locker is empty. Register your first device below.
                </p>
              </div>
            )}
            {devices?.map((d) => (
              <div
                key={d.id}
                className="bg-gray-50 rounded-2xl p-5 shadow-md border-l-4 border-teal-400 flex justify-between items-start gap-4"
              >
                <div className="flex gap-4 items-start">
                  <span className="text-3xl">{typeIcon[d.device_type] || "🔌"}</span>
                  <div>
                    <p className="font-extrabold text-blue-900">{d.nickname}</p>
                    <p className="text-gray-500 text-sm">
                      {typeLabel[d.device_type]}
                      {d.model ? ` · ${d.model}` : ""}
                      {d.os ? ` · ${d.os}` : ""}
                    </p>
                    {d.notes && <p className="text-gray-500 text-xs mt-1">{d.notes}</p>}
                  </div>
                </div>
                <button
                  onClick={() => removeDevice(d.id)}
                  className="text-red-400 hover:text-red-600 text-sm font-bold flex-shrink-0"
                  aria-label={`Remove ${d.nickname}`}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          {/* add form */}
          <div className="bg-slate-900 rounded-2xl p-1" data-aos="fade-up">
            <div className="bg-white rounded-xl p-6">
              <button
                onClick={() => setShowForm(!showForm)}
                className="w-full flex justify-between items-center font-extrabold text-blue-900"
              >
                <span>➕ Register a device</span>
                <span className={`transition-transform ${showForm ? "rotate-180" : ""}`}>▾</span>
              </button>
              {showForm && (
                <form onSubmit={addDevice} className="grid gap-4 mt-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Nickname — e.g. 'John's MacBook Air'"
                      value={form.nickname}
                      onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                      className="px-4 py-3 rounded-xl border border-gray-300 text-sm"
                      maxLength={60}
                      required
                    />
                    <select
                      value={form.deviceType}
                      onChange={(e) => setForm({ ...form, deviceType: e.target.value })}
                      className="px-4 py-3 rounded-xl border border-gray-300 text-sm"
                      aria-label="Device type"
                    >
                      <option value="laptop">Laptop</option>
                      <option value="computer">Desktop</option>
                      <option value="phone">Phone</option>
                      <option value="tablet">Tablet</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Model (e.g. MacBook Air M1)"
                      value={form.model}
                      onChange={(e) => setForm({ ...form, model: e.target.value })}
                      className="px-4 py-3 rounded-xl border border-gray-300 text-sm"
                      maxLength={100}
                    />
                    <input
                      type="text"
                      placeholder="Operating system (e.g. macOS, Windows 11)"
                      value={form.os}
                      onChange={(e) => setForm({ ...form, os: e.target.value })}
                      className="px-4 py-3 rounded-xl border border-gray-300 text-sm"
                      maxLength={100}
                    />
                  </div>
                  <textarea
                    placeholder="Notes for your technician (optional) — e.g. 'Runs slow when printing'"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="px-4 py-3 rounded-xl border border-gray-300 text-sm"
                    rows={3}
                    maxLength={500}
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 rounded-xl bg-blue-900 text-white font-bold hover:bg-blue-800"
                  >
                    🔐 Add to Locker
                  </button>
                </form>
              )}
            </div>
          </div>

          <div className="mt-10 text-center" data-aos="fade-up">
            <Link
              to="/remote"
              className="inline-block px-6 py-3 rounded-xl bg-blue-900 text-white font-bold hover:bg-blue-800 transition"
            >
              🏠 Schedule a ZelConnect Remote Session
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DeviceLocker;
